import { defineComponent as _defineComponent } from 'vue'
import '@/scss/parts/search/global-search.scss';
import { AiChatMessage } from '@/@types/AiChatMessage';
import { ArticleEditMode } from '@/@types/ArticleEditMode';
import { SearchCategory } from '@/@types/enums/SearchCategory';
import { SearchMode } from '@/@types/SearchMode';
import SearchAiChat from '@/components/layout/inputs/GlobalSearch/SearchAiChat.vue';

import InlineIcon from '@/components/layout/shared/InlineIcon.vue';
import githubIcon from '@/assets/img/integrations/github.svg';
import bitbucketIcon from '@/assets/img/integrations/bitbucket.svg';
import confluenceIcon from '@/assets/img/integrations/confluence.svg';
import gdocsIcon from '@/assets/img/integrations/gdocs.svg';
import helper from '@/helper';
import { Article } from '@/models/article/Article';
import { Paragraph } from '@/models/article/Paragraph';
import { SearchByTextProperty } from '@/models/SearchByText';
import { Workspace } from '@/models/Workspace';

import router from '@/router';
import apiService from '@/services/ApiService';
import notificationService from '@/services/NotificationService';
import articlePlayerProvider from '@/services/ui-providers/ArticlePlayerProvider';
import urlService from '@/services/UrlService';
import {
  commitSetArticleIdToExpand,
  commitSetAutoPlay,
  commitSetVideoPlayerTimeToSet,
} from '@/store/commits/sharedCommits';
import { Mutations } from '@/store/mutations';

import { computed, nextTick, ref, watch } from 'vue';
import { useRoute } from 'vue-router/composables';
import SearchHistoryList from '@/components/layout/inputs/GlobalSearch/SearchHistoryList.vue';
import useStore from '@/composables/useStore';


export default /*#__PURE__*/_defineComponent({
  __name: 'GlobalSearchSuggestionList',
  props: {
    suggestions: null,
    searchHistory: null,
    aiSuggestions: null,
    relatedAiSources: null,
    isSuggestionsLoading: { type: Boolean },
    isAiSuggestionsLoading: { type: Boolean },
    isSearchHistoryLoading: { type: Boolean },
    searchState: null,
    searchMode: null,
    isSuggestionsOpened: { type: Boolean },
    hasBottomReached: { type: Boolean },
    triggerQuery: null
  },
  emits: ["onBottomReachedChange", "onSearchReset"],
  setup(__props: any, { emit }: { emit: ({
  (e: 'onBottomReachedChange', value: boolean): boolean;
  (e: 'onSearchReset'): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  // Default search results
  suggestions: Record<string, any>[];
  searchHistory: Record<string, any>;

  // AI
  aiSuggestions: AiChatMessage[];
  relatedAiSources: Record<string, any>[];

  // Loader states
  isSuggestionsLoading: boolean;
  isAiSuggestionsLoading: boolean;
  isSearchHistoryLoading: boolean;

  searchState: string;
  searchMode: SearchMode | null;
  isSuggestionsOpened: boolean;
  hasBottomReached: boolean;
  triggerQuery: Function;
}





const aiSuggestionsContainer = ref<HTMLElement | null>(null);
const aiChatContainer = ref<any | null>(null);

const isPageLoading = ref(false);
const isDragging = ref(false);
const startTopScrollbar = ref();

const scrollbar = ref<HTMLElement>();
const thumb = ref<HTMLElement>();

const store = useStore();
const routes = useRoute();

const SOURCE_ICONS: Record<string, string> = {
  GITHUB: githubIcon,
  BITBUCKET: bitbucketIcon,
  CONFLUENCE: confluenceIcon,
  GDRIVE: gdocsIcon,
};
const sourceIconUrl = (type: string | null): string | null => (type ? SOURCE_ICONS[type.toUpperCase()] ?? null : null);

const getVideoPlayerRef = computed(() => articlePlayerProvider.videoPlayer);

const showCannotNavigateNotification = () => notificationService.warning('Can not navigate to this item');

const saveToSearchHistory = async (searchItem: any) => {
  if (!props.searchHistory?._id) {
    await apiService.globalSearch.createSearchHistory(searchItem);
  } else {
    await apiService.globalSearch.updateSearchHistory(searchItem, props.searchHistory?._id);
  }
};

const handleSelectAiSource = async (item: Record<string, any>) => {
  const isDraft = item.isDraft as boolean;

  item.category = !isDraft ? SearchCategory.ARTICLE : SearchCategory.DRAFT;

  await saveToSearchHistory(item);

  switch (isDraft) {
    case true:
      await router.push(urlService.getDraftViewRoute({ id: item.draftId }));
      return;
    case false: {
      const article = Article.fromJson({ id: item.articleId, ...item });
      const res = await apiService.workspace.get(item.workspace);
      const workspace: Workspace = res.data.workspace;

      if (article && workspace) {
        commitSetArticleIdToExpand(article.id);
        commitSetAutoPlay({ articleId: article.shortId, workspaceId: workspace.shortId });
        await router.push(urlService.getArticleViewRoute(article, workspace, null));
        return;
      }
      break;
    }
  }
};

const handleSelect = async (item: Record<string, any>) => {
  if (!item.category) return false;

  emit('onSearchReset');

  if (!item?.isSaveHistory) {
    await saveToSearchHistory(item);
  }

  switch (item.category) {
    case SearchCategory.WORKSPACE: {
      const workspace = Workspace.parse(item);
      await router.push(urlService.getWorkspaceViewRoute(workspace));
      return;
    }
    case SearchCategory.ARTICLE: {
      const article = Article.fromJson(item);
      const res = await apiService.workspace.get(item.workspace);
      const workspace: Workspace = res.data.workspace;

      if (article && workspace) {
        commitSetArticleIdToExpand(article.id);
        commitSetAutoPlay({ articleId: article.shortId, workspaceId: workspace.shortId });
        await router.push(urlService.getArticleViewRoute(article, workspace, null));
        return;
      }
      break;
    }
    case SearchCategory.DRAFT: {
      const draft = item.isSaveHistory && item?.draftId ? { id: item.draftId } : item;
      if (draft) {
        await router.push(urlService.getDraftViewRoute(draft));
        return;
      }
      break;
    }
    case SearchCategory.DRAFT_NOTES: {
      const draft = item;
      if (draft) {
        const searchByTextProperties = item.foundTextProperties.map((property: SearchByTextProperty) => {
          Object.entries(helper.symbols).forEach(([key, value]) => {
            property.textToFind = property.textToFind.replaceAll(key, value);
          });
          return property;
        });

        store.commit(Mutations.SET_ARTICLE_TEXT_PROPS_TO_FIND, searchByTextProperties);
        // Use notes mode, because the result of this search item always refers to the draft text
        await router.push(urlService.getDraftViewRoute(draft, ArticleEditMode.NOTES));
        return;
      }
      break;
    }
    case SearchCategory.PARAGRAPH: {
      const article = Article.fromJson(item.article);
      const res = await apiService.workspace.get(item.article.workspace);
      const workspace: Workspace = res.data.workspace;
      const paragraph = Paragraph.parse(item);

      if (article && workspace) {
        commitSetArticleIdToExpand(article.id);
        commitSetAutoPlay({
          articleId: article.shortId,
          workspaceId: workspace.shortId,
          paragraphSlug: paragraph.getSlug(),
        });
        router.push(urlService.getParagraphViewRoute(paragraph.getSlug(), article, workspace));

        return;
      }
      break;
    }
    case SearchCategory.ARTICLE_NOTES: {
      const article = Article.fromJson(item);
      const res = await apiService.workspace.get(item.workspace);
      const workspace: Workspace = res.data.workspace;

      if (article && workspace) {
        const searchByTextProperties = item.foundTextProperties.map((property: SearchByTextProperty) => {
          Object.entries(helper.symbols).forEach(([key, value]) => {
            property.textToFind = property.textToFind.replaceAll(key, value);
          });
          return property;
        });

        store.commit(Mutations.SET_ARTICLE_TEXT_PROPS_TO_FIND, searchByTextProperties);
        commitSetArticleIdToExpand(article.id);
        commitSetAutoPlay({
          articleId: article.shortId,
          workspaceId: workspace.shortId,
        });

        await router.push(urlService.getArticleViewRoute(article, workspace, ArticleEditMode.NOTES));
        return;
      }
      break;
    }
    case SearchCategory.TAG: {
      const article = Article.fromJson(item);
      const res = await apiService.workspace.get(item.workspace);
      const workspace: Workspace = res.data.workspace;
      if (article && workspace) {
        commitSetArticleIdToExpand(article.id);
        commitSetAutoPlay(null);
        router.push(urlService.getArticleViewRoute(article, workspace));
        return;
      }
      break;
    }
    case SearchCategory.ARTICLE_VIDEO: {
      const article = Article.fromJson(item);
      const res = await apiService.workspace.get(item.workspace);
      const workspace: Workspace = res.data.workspace;
      if (article && workspace) {
        commitSetArticleIdToExpand(article.id);
        commitSetAutoPlay(null);
        await router.push(urlService.getArticleViewRoute(article, workspace, ArticleEditMode.VIDEO));

        const videoPlayerRef = getVideoPlayerRef.value;

        if (videoPlayerRef) {
          await videoPlayerRef.stop();
          if (routes.params.articleId === article.id) videoPlayerRef.updatePlayerRef();
          videoPlayerRef.setTime(item.startTime);
          commitSetVideoPlayerTimeToSet(null);
        }

        commitSetVideoPlayerTimeToSet(item.startTime);
        return;
      }
      break;
    }

    case SearchCategory.DRAFT_VIDEO: {
      const draft = item;
      if (draft) {
        const videoPlayerRef = getVideoPlayerRef.value;
        if (videoPlayerRef) {
          videoPlayerRef.setTime(item.startTime);
        }

        commitSetVideoPlayerTimeToSet(item.startTime);

        await router.push(urlService.getDraftViewRoute(draft, ArticleEditMode.VIDEO));
        return;
      }
      break;
    }
  }

  showCannotNavigateNotification();
};

const handleScroll = async (event: any) => {
  const target = event.target as HTMLElement;
  const distanceToBottom = target.scrollHeight - (target.scrollTop + target.clientHeight);

  const threshold = 250;

  const maxThumbTop = scrollbar.value?.offsetHeight - thumb.value.offsetHeight || 0;
  const scrollPercentage = target.scrollTop / (target.scrollHeight - target.offsetHeight);
  const newThumbTop = maxThumbTop * scrollPercentage;

  updateThumbPosition(newThumbTop);

  // if user scrolled to 25 item, fetch items
  if (distanceToBottom > threshold || props.hasBottomReached) return;
  if (props.suggestions.length <= 23) isPageLoading.value = false;

  emit('onBottomReachedChange', true);

  isPageLoading.value = true;
  try {
    // Use props to fix emit event executing twice
    await props.triggerQuery();
  } finally {
    isPageLoading.value = false;
    emit('onBottomReachedChange', false);
  }
};

const handleThumbMouseDown = (event: MouseEvent) => {
  isDragging.value = true;
  startTopScrollbar.value = event.clientY - (thumb.value?.getBoundingClientRect().top || 0);

  const handleMouseMove = (event: MouseEvent) => {
    if (!isDragging) return;
    const mouseY = event.clientY;
    const newTop = mouseY - scrollbar.value?.getBoundingClientRect().top - startTopScrollbar.value;
    updateThumbPosition(newTop);
    updateScrollPosition();
  };

  const handleMouseUp = () => {
    isDragging.value = false;
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
  };

  document.addEventListener('mousemove', handleMouseMove);
  document.addEventListener('mouseup', handleMouseUp);
};

const handleScrollbarMouseDown = (event: MouseEvent) => {
  if (!isDragging && scrollbar.value) {
    const clickY = event.clientY - scrollbar.value.getBoundingClientRect().top;
    const thumbHeight = thumb.value?.offsetHeight || 0;
    const thumbTop = clickY - thumbHeight / 2;

    updateThumbPosition(thumbTop);
    updateScrollPosition();
  }
};

const updateThumbPosition = (newTop: number) => {
  if (!thumb.value) return;

  const maxTop = scrollbar.value?.offsetHeight - thumb.value.offsetHeight || 0;
  const clampedTop = Math.max(0, Math.min(newTop, maxTop));
  thumb.value.style.transform = `translateY(${clampedTop}px)`;
};
const updateScrollPosition = () => {
  if (!scrollbar.value && !thumb.value) return;

  const thumbRect = thumb.value.getBoundingClientRect();
  const thumbTop = thumbRect.top - scrollbar.value.getBoundingClientRect().top;
  const maxThumbTop = scrollbar.value.offsetHeight - thumb.value.offsetHeight;
  const scrollPercentage = maxThumbTop > 0 ? thumbTop / maxThumbTop : 0;
  const maxScrollTop = scrollbar.value.scrollHeight - scrollbar.value.offsetHeight;
  scrollbar.value.scrollTop = scrollPercentage * maxScrollTop;
};

watch(
  () => props.aiSuggestions,
  async () => {
    await nextTick(); // Ensure the DOM is updated
    if (aiSuggestionsContainer.value && aiChatContainer.value) {
      const items = aiChatContainer.value.$el.querySelectorAll('div');

      if (items.length > 0) {
        const elIndex = items.length <= 1 || props.isAiSuggestionsLoading ? items.length - 1 : items.length - 2;

        // Get the last <span> element in the chat list
        const lastItem = items[elIndex];
        // Scroll to the last item
        lastItem.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  },
  { deep: true }
);

return { __sfc: true,props, emit, aiSuggestionsContainer, aiChatContainer, isPageLoading, isDragging, startTopScrollbar, scrollbar, thumb, store, routes, SOURCE_ICONS, sourceIconUrl, getVideoPlayerRef, showCannotNavigateNotification, saveToSearchHistory, handleSelectAiSource, handleSelect, handleScroll, handleThumbMouseDown, handleScrollbarMouseDown, updateThumbPosition, updateScrollPosition, SearchCategory, SearchMode, SearchAiChat, InlineIcon, SearchHistoryList }
}

})